import React, { ReactNode, InputHTMLAttributes, useRef, ChangeEventHandler, useCallback } from 'react';
import classNames from 'classnames';
import { Button, ButtonProps } from '../button';
import { IconUpload } from '../icons/generated';
import { useProviderConfig } from '../provider';
import { hidden } from '../../styles';
import styled from '@emotion/styled';

export interface FileUploadButtonProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'multiple'> {
  /** @default '파일 업로드' */
  label: ReactNode;
  /** @default false */
  multiple?: boolean;
  loading?: boolean;
  buttonProps?: Omit<ButtonProps, 'onClick' | 'disabled' | 'loading'>;
  onFileUpload?: (file: File | File[]) => void;
}

export const FileUploadButton = ({
  accept = 'image/*',
  label,
  disabled,
  className,
  multiple = false,
  loading,
  buttonProps,
  onFileUpload,
  ...props
}: FileUploadButtonProps) => {
  const { locale } = useProviderConfig();
  const uploadRef = useRef<HTMLInputElement | null>(null);

  const handleClickUpload = () => {
    if (!uploadRef.current) return;
    uploadRef.current.click();
  };

  const handleFileChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = multiple ? Array.from(event.target.files) : event.target.files[0];
        event.target.value = '';
        onFileUpload?.(file);
      }
    },
    [onFileUpload],
  );

  return (
    <>
      <label hidden htmlFor={props.id}>
        <HiddenInput
          type='file'
          accept={accept}
          disabled={disabled}
          multiple={multiple}
          className={classNames('pds-button', className)}
          onChange={handleFileChange}
          {...props}
          ref={uploadRef}
        />
      </label>
      <Button
        disabled={disabled}
        loading={loading}
        startIcon={<IconUpload />}
        onClick={handleClickUpload}
        type='button'
        {...buttonProps}
      >
        {label ?? locale?.FileUpload.label}
      </Button>
    </>
  );
};

const HiddenInput = styled.input`
  ${hidden};
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
